import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Application, PatchApplicationStudentResponse } from '@ab/ams-school-api/types/private/v1';
import { config } from '~/config';
import { useRequest } from '~/lib/useRequest';
import { hasKey } from '~/utils';

export type PatchStudentAssignmentPayload = {
  studentAccountExternalId: string;
};

export function usePatchStudentAssignment(id: string) {
  const request = useRequest();
  const queryClient = useQueryClient();
  const { isLoading, error, mutateAsync } = useMutation(
    ['application', id],
    async (payload: PatchStudentAssignmentPayload) => {
      const response = await request<PatchApplicationStudentResponse>(
        `${config.apiHost}/private/v1/applications/${id}/student`,
        {
          method: 'PATCH',
          body: {
            data: {
              id,
              type: 'application',
              attributes: {
                studentAccountExternalId: payload.studentAccountExternalId,
              },
            },
          },
          isExpectedResponse,
        }
      );

      const app = response.data;
      queryClient.invalidateQueries(['application', id]);

      return {
        ...app.attributes,
        id: app.id,
      } as Application;
    }
  );

  return {
    patchStudentAssignment: mutateAsync,
    patchStudentAssignmentError: error,
    isPatchingStudentAssignment: isLoading,
  };
}

function isExpectedResponse(res: unknown): res is PatchApplicationStudentResponse {
  return Boolean(res && typeof res === 'object' && hasKey('data', res));
}
